import { mediaScope } from "./chatMediaCache";

const FRESH_MS = 3000;
const contexts = new Map(), listeners = new Set();
const pendingInvalidations = new Set();
const AUTH_KEYS = new Set(["token","role","student_phone","guest_phone","parent_phone","advisor_id","supporter_id","supervisor_id","admin_id"]);
const session = (role) => `${mediaScope(role)}|${localStorage.getItem("token") || ""}`;
const digest = async (value) => [...new Uint8Array(await window.crypto.subtle.digest("SHA-256",new TextEncoder().encode(value)))].map((b) => b.toString(16).padStart(2,"0")).join("");

export function nextChatPollDelay(period) {
  return Math.max(100,period - Date.now() % period);
}

async function fetchJson(url,options) {
  const response = await fetch(url,{ cache:"no-store",...options });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(typeof data?.detail === "string" ? data.detail : "ارتباط با بخش پیام‌ها انجام نشد");
    error.status = response.status; throw error;
  }
  return data;
}

export function isSharedChatRead(url,options = {}) {
  const path = new URL(url,window.location.origin).pathname;
  const method = (options.method || "GET").toUpperCase();
  return !options.signal && ((method === "GET" && /\/chat\/(unread-count|upload-limits|conversations\/\d+\/messages)$/.test(path))
    || (method === "POST" && /\/chat\/conversations\/(page|sync|\d+\/messages\/sync)$/.test(path) && typeof options.body === "string"));
}

export function changesChatState(url,options = {}) {
  const path = new URL(url,window.location.origin).pathname;
  const method = (options.method || "GET").toUpperCase();
  return (method === "POST" && /\/chat\/conversations\/\d+\/(messages|read)$/.test(path))
    || (["PATCH","DELETE"].includes(method) && /\/chat\/(messages|admin\/pension-reports)\/\d+$/.test(path));
}

// Injectable transport for deterministic multi-tab tests. No media, token,
// response body or lock lease is written to localStorage/IndexedDB.
export function createChatRequestBroker({ scopeHash,channel,locks,hash = digest,fetcher = fetchJson,
  isCurrent = () => true,onUnread = () => {},onInvalidated = () => {},peerWait = 80,lockWait = 3500 }) {
  const id = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  const cache = new Map(), inFlight = new Map(), waiters = new Map(), controllers = new Set();
  let closed = false, epoch = "0";
  const valid = () => !closed && isCurrent();
  const send = (message) => { if (valid()) try { channel.postMessage({ ...message,from:id }); } catch (_) {} };
  const peek = (key) => {
    const entry = cache.get(key);
    if (entry && entry.until > Date.now() && entry.epoch === epoch) return entry;
    cache.delete(key); return null;
  };
  const advance = (value) => {
    if (typeof value === "string" && /^\d{16}:[a-zA-Z0-9-]{1,64}$/.test(value) && value > epoch) { epoch = value; cache.clear(); return true; }
    return false;
  };
  const remember = (key,data,until,provider) => {
    let bytes;
    try { bytes = JSON.stringify(data).length * 2; } catch (_) { return; }
    if (bytes > 512 * 1024) return;
    cache.delete(key);
    for (const [old,entry] of cache) if (entry.until <= Date.now()) cache.delete(old);
    cache.set(key,{ data,until,epoch,provider,bytes });
    let total = [...cache.values()].reduce((sum,entry) => sum + entry.bytes,0);
    while (cache.size > 8 || total > 2 * 1024 * 1024) {
      const first = cache.keys().next().value; total -= cache.get(first).bytes; cache.delete(first);
    }
    waiters.get(key)?.forEach((resolve) => resolve());
    if (Number.isFinite(data?.unread_count)) onUnread(data.unread_count);
  };
  const receive = ({ data:message }) => {
    if (!valid() || !message || message.from === id || (message.to && message.to !== id)) return;
    if (message.type === "invalidate") {
      if (advance(message.epoch)) onInvalidated();
    } else if (message.type === "get" && typeof message.key === "string") {
      advance(message.epoch);
      const entry = peek(message.key);
      if (entry?.provider === id) send({ type:"result",to:message.from,key:message.key,data:entry.data,until:entry.until,epoch });
      else send({ type:"epoch",to:message.from,epoch });
    } else if (message.type === "epoch") advance(message.epoch);
    else if (message.type === "result" && typeof message.key === "string" && message.until > Date.now() && message.epoch >= epoch) {
      advance(message.epoch);
      remember(message.key,message.data,Math.min(message.until,Date.now() + FRESH_MS),message.from);
    }
  };
  channel.addEventListener("message",receive);
  const ask = (key) => new Promise((resolve) => {
    const waiting = waiters.get(key) || new Set(); waiters.set(key,waiting);
    let timer;
    const finish = () => { clearTimeout(timer); waiting.delete(finish); if (!waiting.size) waiters.delete(key); resolve(); };
    waiting.add(finish); timer = setTimeout(finish,peerWait);
    send({ type:"get",key,epoch });
  });
  const read = async (url,options = {}) => {
    if (!valid()) throw new DOMException("Session changed","AbortError");
    const key = await hash(JSON.stringify([new URL(url,window.location.origin).href,(options.method || "GET").toUpperCase(),options.body || ""]));
    if (!valid()) throw new DOMException("Session changed","AbortError");
    const recent = peek(key);
    if (recent) return recent.data;
    if (inFlight.has(key)) return inFlight.get(key);
    const job = (async () => {
      const waiting = new AbortController(); controllers.add(waiting);
      let entered = false;
      const timeout = setTimeout(() => waiting.abort(),lockWait);
      const fetchAndShare = async () => {
        const started = epoch;
        const controller = new AbortController(); controllers.add(controller);
        try {
          const data = await fetcher(url,{ ...options,signal:controller.signal });
          if (!valid()) throw new DOMException("Session changed","AbortError");
          if (started === epoch) {
            const until = Date.now() + FRESH_MS;
            remember(key,data,until,id);
            if (peek(key)) send({ type:"result",key,data,until,epoch });
          }
          return data;
        } finally { controllers.delete(controller); }
      };
      try {
        return await locks.request(`alef-chat-read:${scopeHash}:${key}`,{ signal:waiting.signal },async () => {
          entered = true; clearTimeout(timeout);
          if (!valid()) throw new DOMException("Session changed","AbortError");
          const cached = peek(key); if (cached) return cached.data;
          await ask(key);
          if (!valid()) throw new DOMException("Session changed","AbortError");
          const peer = peek(key); if (peer) return peer.data;
          return fetchAndShare();
        });
      } catch (error) {
        // A frozen/closing tab must never prevent this tab from receiving chat.
        if (!entered && valid()) return fetchAndShare();
        throw error;
      } finally { clearTimeout(timeout); controllers.delete(waiting); }
    })();
    inFlight.set(key,job);
    try { return await job; } finally { if (inFlight.get(key) === job) inFlight.delete(key); }
  };
  return { read,
    invalidate() { if (!valid()) return; epoch = `${String(Math.max(Date.now(),Number(epoch.split(":")[0]) + 1)).padStart(16,"0")}:${id}`; cache.clear(); send({ type:"invalidate",epoch }); onInvalidated(); },
    close() { closed = true; controllers.forEach((controller) => controller.abort()); controllers.clear(); cache.clear();
      waiters.forEach((waiting) => [...waiting].forEach((finish) => finish())); waiters.clear();
      channel.removeEventListener("message",receive); channel.close(); },
  };
}

async function brokerFor(role) {
  if (!localStorage.getItem("token") || !mediaScope(role) || !navigator.locks?.request || typeof BroadcastChannel === "undefined" || !window.crypto?.subtle) return null;
  const snapshot = session(role);
  for (const [key,context] of contexts) if (context.snapshot !== session(context.role)) { context.broker?.close(); contexts.delete(key); }
  let context = contexts.get(snapshot);
  if (!context) {
    context = { role,snapshot,broker:null };
    context.ready = (async () => {
      const scopeHash = await digest(snapshot);
      if (session(role) !== snapshot || contexts.get(snapshot) !== context) return null;
      try {
        context.broker = createChatRequestBroker({ scopeHash,locks:navigator.locks,
          channel:new BroadcastChannel(`alef-chat-read-v1:${scopeHash}`),isCurrent:() => session(role) === snapshot,
          onUnread:(count) => listeners.forEach((listener) => listener(role,count)),
          onInvalidated:() => { listeners.forEach((listener) => listener(role,null)); window.dispatchEvent(new CustomEvent("chat-shared-invalidated",{ detail:{ role } })); },
        });
        if (pendingInvalidations.delete(snapshot)) context.broker.invalidate();
        return context.broker;
      } catch (_) { return null; }
    })();
    contexts.set(snapshot,context);
  }
  return context.ready;
}

export async function chatJsonRequest(role,url,options = {}) {
  const snapshot = session(role);
  if (isSharedChatRead(url,options)) {
    const headers = Object.fromEntries(Object.entries(options.headers || {}).map(([key,value]) => [key.toLowerCase(),value]));
    if (headers.authorization !== `Bearer ${localStorage.getItem("token") || ""}` || headers["x-actor-role"] !== role) return fetchJson(url,options);
    let broker;
    try { broker = await brokerFor(role); } catch (_) { broker = null; }
    if (session(role) !== snapshot) throw new DOMException("Session changed","AbortError");
    return broker ? broker.read(url,options) : fetchJson(url,options);
  }
  const data = await fetchJson(url,options);
  if (session(role) === snapshot && changesChatState(url,options)) invalidateChatReads(role);
  return data;
}

export function onSharedChatUnread(listener) { listeners.add(listener); return () => listeners.delete(listener); }
export function invalidateChatReads(role) {
  const key = session(role), context = contexts.get(key);
  if (context?.broker) context.broker.invalidate();
  else {
    if (pendingInvalidations.size > 8) pendingInvalidations.clear();
    pendingInvalidations.add(key);
    brokerFor(role).catch(() => {});
  }
}
export function clearSharedChatRequests() { contexts.forEach((context) => context.broker?.close()); contexts.clear(); pendingInvalidations.clear(); }
window.addEventListener("storage",(event) => { if (event.key == null || AUTH_KEYS.has(event.key)) clearSharedChatRequests(); });
window.addEventListener("pagehide",clearSharedChatRequests);
